/**
 * useCommunityLiveListener — produces the three live-listener handlers
 * (`added` / `modified` / `removed`) for the `useCommunity` orchestration hook
 * (Task 10).
 *
 * Extracted from the main community hook to keep both files below the size
 * threshold (`presentation.md` § Component size). Modeled on the web chat port
 * (`useChatLiveListener`) for structure, MINUS the chat-only concerns that do
 * not exist on a community wall:
 *   - NO mark-seen / read-receipt step (a community wall has no read receipts —
 *     `community_screen.dart` has no `markSeen` call).
 *   - NO E2EE decryption fan-out (community messages are not encrypted).
 *   - NO `openTimestampForMessageId` map (community rows show no per-bubble
 *     "tap to reveal time" affordance).
 *   - NO own-message insert branch. Chat's `added` branch
 *     (`chat_screen.dart:527-540`) inserts own messages on `idFrom ==
 *     currentFirebaseId`, but community's `added` branch
 *     (`community_screen.dart:299-336`) has NO own-vs-peer discriminator — it
 *     routes purely on app-lifecycle + scroll position. We follow community,
 *     not chat: there is no `currentUserId` arg here.
 *
 * The handlers are bound exactly ONCE in the consumer
 * (`SubscribeToCommunityMessagesUseCase.execute(...)`, Task 6) so they cannot
 * close over state — they read from refs (`isAtBottomRef`, `isTabVisibleRef`)
 * and write via the setters passed in as arguments.
 *
 * Flutter source of truth: `community_screen.dart:293-358` (the three
 * `DocumentChangeType` branches inside `subscribeToLiveMessages`).
 */
import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';

import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { sortCommunityMessagesByCreatedDesc } from '@/domain/utils/communityUtils';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

export interface UseCommunityLiveListenerArgs {
  /**
   * Ref tracking whether the user is currently pinned to the bottom of the
   * (reversed) list. The `added` branch routes to the visible list when true,
   * or to the unread bucket when false.
   *
   * Web mirror of Flutter's `_bottomVisibleIndex <= 3` insert threshold
   * (`community_screen.dart:312`). Flutter has TWO thresholds — `<= 3` for the
   * insert and the separate `>= 5` for `_isScrolledUp`
   * (`community_screen.dart:235`) — because the Flutter list reports a
   * continuous visible-index and the gap absorbs partial scroll. The web list
   * (Task 11) collapses that pair into the single `isAtBottomRef` boolean,
   * exactly as the chat listener collapses Flutter's `<= 100` / `<= 0` pair.
   */
  isAtBottomRef: MutableRefObject<boolean>;
  /**
   * Ref tracking whether the current user's browser tab is visible. Web mirror
   * of Flutter `_appLifecycleState` (visible ⇔ resumed / `index == 0`, hidden
   * ⇔ paused / `index == 2`, `community_screen.dart:106-127`). The `added`
   * branch checks this FIRST (mirroring Flutter's background-first branch order
   * at `community_screen.dart:308-335`): when the tab is hidden the incoming
   * message is routed to the unread bucket — never inserted — REGARDLESS of
   * `isAtBottomRef`. The 3 s unread pill is NOT raised while hidden; the resume
   * branch (Task 10) re-raises it when the user returns.
   */
  isTabVisibleRef: MutableRefObject<boolean>;
  setMessages: Dispatch<SetStateAction<CommunityChatMessageEntity[]>>;
  setUnreadItems: Dispatch<SetStateAction<CommunityChatMessageEntity[]>>;
  /**
   * Setter for the transient unread-pill visibility flag — flipped to `true` on
   * every message-while-scrolled-away arrival (tab visible), and back to
   * `false` by a 3 s `setTimeout` scheduled here. Mirrors Flutter's
   * `_unreadMessageReceived` (`community_screen.dart:323-334`).
   */
  setUnreadBannerVisible: Dispatch<SetStateAction<boolean>>;
  /**
   * Ref holding the handle of the pending 3 s auto-hide `setTimeout`, so the
   * listener can clear and reschedule on consecutive arrivals (and the
   * page dispose path can clear it on unmount).
   */
  unreadBannerTimerRef: MutableRefObject<number | null>;
}

export interface UseCommunityLiveListenerResult {
  handleAdded: (incoming: CommunityChatMessageEntity) => void;
  handleModified: (incoming: CommunityChatMessageEntity) => void;
  handleRemoved: (incoming: CommunityChatMessageEntity) => void;
}

export const useCommunityLiveListener = (
  args: UseCommunityLiveListenerArgs,
): UseCommunityLiveListenerResult => {
  const {
    isAtBottomRef,
    isTabVisibleRef,
    setMessages,
    setUnreadItems,
    setUnreadBannerVisible,
    unreadBannerTimerRef,
  } = args;

  /**
   * `added` branch (`community_screen.dart:299-336`).
   *
   * 1. Dedupe by `incoming.id`: an optimistic insert lands in the list before
   *    the Firestore write echoes back as an `added` change with the same id;
   *    Flutter replaces the existing entry in-place
   *    (`community_screen.dart:300-306`). We mirror that exactly (functional
   *    setState + splice replacement + re-sort) so the optimistic and
   *    authoritative copies reconcile cleanly — no duplicate row.
   * 2. Otherwise route, mirroring Flutter's background-first ordering
   *    (`community_screen.dart:308-335`), collapsed into the single
   *    `treatAsAtBottom = isAtBottomRef.current && isTabVisibleRef.current`
   *    boolean. Flutter's `added` branch routes PURELY on lifecycle/scroll —
   *    there is NO `userId == currentUserId` own-vs-peer check anywhere in
   *    `community_screen.dart:299-336` (unlike chat's `chat_screen.dart:527-540`
   *    own-message insert). An own message echoed back is reconciled by the
   *    id-dedupe in step 1; an own message with no local optimistic copy (e.g.
   *    a second session viewing the same wall as its author) routes exactly like
   *    any other message:
   *      - tab HIDDEN (web `!isTabVisibleRef.current`, Flutter
   *        `_appLifecycleState.index == 2`, the FIRST branch at
   *        `community_screen.dart:308-311`) → unread bucket only, NO insert, NO
   *        pill timer (re-raised on resume in Task 10). Handled by
   *        `treatAsAtBottom` being false.
   *      - tab VISIBLE and at bottom (`community_screen.dart:312-321`) →
   *        insert + sort.
   *      - tab VISIBLE but scrolled away (`community_screen.dart:322-334`) →
   *        unread bucket + 3 s pill.
   */
  const handleAdded = useCallback(
    (incoming: CommunityChatMessageEntity) => {
      // Collapse Flutter's "background first, then scroll" ordering
      // (`community_screen.dart:308-335`) into one boolean: an incoming message
      // is only treated as "at bottom" (insert) when the user is pinned to the
      // bottom AND the tab is visible. A hidden tab (Flutter
      // `_appLifecycleState.index == 2`) forces this `false` REGARDLESS of
      // scroll position, routing the message to the unread bucket instead.
      const treatAsAtBottom = isAtBottomRef.current && isTabVisibleRef.current;

      let routedToUnread = false;

      setMessages((prev) => {
        // Dedupe against an optimistic copy still in the list
        // (`community_screen.dart:300-306`).
        const existingIdx = prev.findIndex((m) => m.id === incoming.id);
        if (existingIdx !== -1) {
          const next = prev.slice();
          next[existingIdx] = incoming;
          next.sort(sortCommunityMessagesByCreatedDesc);
          return next;
        }

        if (treatAsAtBottom) {
          // Tab visible + at bottom: insert at index 0 + sort
          // (`community_screen.dart:312-321`). No own-vs-peer split — Flutter's
          // `added` branch routes purely on lifecycle/scroll with no
          // `userId == currentUserId` check (see comment block above).
          const next = [incoming, ...prev];
          next.sort(sortCommunityMessagesByCreatedDesc);
          return next;
        }

        // Tab hidden OR scrolled away: defer to the unread bucket
        // (`community_screen.dart:310 / :323`). Done via the separate
        // `setUnreadItems` call below so this updater stays pure for `prev`.
        routedToUnread = true;
        return prev;
      });

      if (!routedToUnread) {
        return;
      }

      // Push to the unread bucket — Flutter `_unreadListItems.add(message)`
      // (`community_screen.dart:310 / :323`). The bucket MUST be appended even
      // while the tab is hidden so the message is tracked and the resume branch
      // (Task 10, Flutter `community_screen.dart:114-125`) can re-show the pill
      // when the user returns.
      setUnreadItems((prev) => [...prev, incoming]);

      // Flutter parity (`community_screen.dart:323-334`):
      // `_unreadMessageReceived = true; setState();` then
      // `Future.delayed(3s, () { _unreadMessageReceived = false; setState(); })`.
      // Each new arrival restarts the 3 s window.
      //
      // Skip the pill visibility + timer while the tab is HIDDEN: that work is
      // invisible and a background `setTimeout` would expire the pill before the
      // user returns. Flutter shows the pill only on resume in this case
      // (`community_screen.dart:114-125`), which Task 10 re-arms via its
      // tab-resume branch. The unread bucket above is still appended so the
      // resume branch has something to preview.
      if (isTabVisibleRef.current) {
        setUnreadBannerVisible(true);
        if (unreadBannerTimerRef.current !== null) {
          window.clearTimeout(unreadBannerTimerRef.current);
        }
        unreadBannerTimerRef.current = window.setTimeout(() => {
          setUnreadBannerVisible(false);
          unreadBannerTimerRef.current = null;
        }, ANIMATION_CONSTANTS.duration.autoHideDelay);
      }
    },
    [
      isAtBottomRef,
      isTabVisibleRef,
      setMessages,
      setUnreadItems,
      setUnreadBannerVisible,
      unreadBannerTimerRef,
    ],
  );

  /**
   * `modified` branch (`community_screen.dart:337-345`).
   *
   * Replace the existing entry in-place (lookup by `id`) and re-sort. No-op when
   * the doc isn't in the local list (e.g. a `modified` for an older page that
   * hasn't been fetched yet).
   */
  const handleModified = useCallback(
    (incoming: CommunityChatMessageEntity) => {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === incoming.id);
        if (idx === -1) return prev;
        const next = prev.slice();
        next[idx] = incoming;
        next.sort(sortCommunityMessagesByCreatedDesc);
        return next;
      });
    },
    [setMessages],
  );

  /**
   * `removed` branch (`community_screen.dart:346-354`).
   *
   * Splice the matching entry (lookup by `id`). No-op when absent.
   */
  const handleRemoved = useCallback(
    (incoming: CommunityChatMessageEntity) => {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === incoming.id);
        if (idx === -1) return prev;
        const next = prev.slice();
        next.splice(idx, 1);
        // No need to re-sort after a delete — the remaining order is unchanged.
        return next;
      });
    },
    [setMessages],
  );

  return { handleAdded, handleModified, handleRemoved };
};
